import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { taxApi } from 'services/apiDigitalTicket/tax';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import { listCompaniesRequest } from 'store/ducks/companies/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';

// Actions
import * as TaxActions from './actions';

// Types
import { TaxTypes } from './types';

function* list({ payload }: ReturnType<typeof TaxActions.listTaxRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(taxApi.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        TaxActions.listTaxSuccess({
          taxList: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('Tax - listTax - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TaxActions.listTaxFailure());

    yield put(DialogsActions.setClose());
  }
}

function* get({ payload }: ReturnType<typeof TaxActions.getTaxRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(taxApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TaxActions.getTaxSuccess({ taxData: response.data }));

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('Tax - getTax - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TaxActions.getTaxFailure());

    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof TaxActions.createTaxRequest>): any {
  try {
    const { type } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(taxApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TaxActions.createTaxSuccess({ taxData: response.data }));

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Imposto criado com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(TaxActions.listTaxRequest({ type }));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('Tax - createTax - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TaxActions.createTaxFailure());

    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof TaxActions.updateTaxRequest>): any {
  try {
    const { type } = payload;
    yield put(DialogsActions.setOpenLoading({ title: 'Editando Imposto' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(taxApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TaxActions.updateTaxSuccess({ taxData: response.data }));

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Imposto alterado com sucesso',
        }),
      );

      yield put(
        TaxActions.listTaxRequest({
          type,
          _includes: 'companies',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('Tax - updateTax - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TaxActions.updateTaxFailure());

    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof TaxActions.deleteTaxRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Deletando Imposto' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(taxApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TaxActions.deleteTaxSuccess());

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Imposto excluído com sucesso',
        }),
      );

      yield put(
        TaxActions.listTaxRequest({
          _includes: 'companies',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('Tax - remove Tax - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TaxActions.deleteTaxFailure());

    yield put(DialogsActions.setClose());
  }
}

function* attach({
  payload,
}: ReturnType<typeof TaxActions.attachTaxRequest>): any {
  try {
    const { type } = payload;

    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const companyId = payload?.companyId;

    companyId && delete payload?.companyId; 

    const response = yield call(taxApi.attach, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TaxActions.attachTaxSuccess());

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Imposto aplicado com sucesso',
        }),
      );

      yield put(
        listCompaniesRequest({
          body: {
            id: companyId,
            _includes: 'tax',
            _limit: 9999,
          },
        }),
      );
      yield put(
        TaxActions.listTaxRequest({
          type,
          _includes: 'companies',
          _limit: 9999,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('Tax - attach Tax - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TaxActions.attachTaxFailure());

    yield put(DialogsActions.setClose());
  }
}

function* detach({
  payload,
}: ReturnType<typeof TaxActions.detachTaxRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(taxApi.detach, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(TaxActions.detachTaxSuccess());

      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Imposto removido com sucesso',
        }),
      );

      yield put(
        listCompaniesRequest({
          body: {
            _includes: 'tax',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('Tax - detach Tax - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(TaxActions.detachTaxFailure());

    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const taxSagas = [
  takeLatest(TaxTypes.LIST_TAX_REQUEST, list),
  takeLatest(TaxTypes.GET_TAX_REQUEST, get),
  takeLatest(TaxTypes.CREATE_TAX_REQUEST, create),
  takeLatest(TaxTypes.UPDATE_TAX_REQUEST, update),
  takeLatest(TaxTypes.DELETE_TAX_REQUEST, remove),
  takeLatest(TaxTypes.ATTACH_TAX_REQUEST, attach),
  takeLatest(TaxTypes.DETACH_TAX_REQUEST, detach),
];
